import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BeatLoader } from 'react-spinners';

import {
  ContentResourceType,
  getContentResources,
  IContentResource,
} from '../../services/api';
import { useToast } from '../../hooks/toast';
import {
  Container,
  Filters,
  Header,
  Pagination,
  ResourceTable,
  Status,
} from './styles';

const TYPE_LABELS: Record<ContentResourceType, string> = {
  video: 'Videoaula',
  podcast: 'Podcast',
  infographic: 'Infográfico',
  image: 'Imagem',
};

const MyResources: React.FC = () => {
  const { addToast } = useToast();
  const [resources, setResources] = useState<IContentResource[]>([]);
  const [search, setSearch] = useState('');
  const [type, setType] = useState<ContentResourceType | ''>('');
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const pageSize = 20;

  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const { data } = await getContentResources({
          title: search || undefined,
          type: type || undefined,
          page,
          pageSize,
        });
        setResources(data.content);
        setCount(data.count);
      } catch (error) {
        addToast({
          title: 'Ops ...',
          description: 'Não foi possível carregar os recursos.',
          type: 'error',
        });
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [addToast, page, search, type]);

  const pageCount = Math.max(1, Math.ceil(count / pageSize));

  return (
    <Container className="slideDown">
      <Header>
        <h1>Meus Recursos</h1>
        <div>
          <Link className="secondary" to="/dashboard/my-units">
            Minhas Unidades
          </Link>
          <Link to="/dashboard/my-resources/new">Novo Recurso</Link>
        </div>
      </Header>

      <Filters>
        <input
          aria-label="Pesquisar recursos"
          value={search}
          onChange={event => {
            setSearch(event.target.value);
            setPage(1);
          }}
          placeholder="Pesquisar pelo título"
        />
        <select
          aria-label="Filtrar pelo tipo"
          value={type}
          onChange={event => {
            setType(event.target.value as ContentResourceType | '');
            setPage(1);
          }}
        >
          <option value="">Todos os tipos</option>
          <option value="video">Videoaulas</option>
          <option value="podcast">Podcasts</option>
          <option value="infographic">Infográficos</option>
          <option value="image">Imagens</option>
        </select>
      </Filters>

      <ResourceTable>
        {loading ? (
          <div className="empty">
            <BeatLoader size={14} />
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Recurso</th>
                <th>Tipo</th>
                <th>Versão</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {resources.length === 0 && (
                <tr>
                  <td className="empty" colSpan={4}>
                    Nenhum recurso encontrado.
                  </td>
                </tr>
              )}
              {resources.map(resource => {
                const latestVersion = resource.versions[0];
                return (
                  <tr key={resource.id}>
                    <td>{resource.title}</td>
                    <td>{TYPE_LABELS[resource.type]}</td>
                    <td>{latestVersion?.version || '-'}</td>
                    <td>
                      <Status className={latestVersion?.status || 'draft'}>
                        {latestVersion?.status === 'publish'
                          ? 'Publicado'
                          : 'Rascunho'}
                      </Status>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </ResourceTable>

      <Pagination>
        <button
          type="button"
          disabled={page === 1}
          onClick={() => setPage(currentPage => currentPage - 1)}
        >
          Anterior
        </button>
        <span>
          Página {page} de {pageCount} ({count} recursos)
        </span>
        <button
          type="button"
          disabled={page >= pageCount}
          onClick={() => setPage(currentPage => currentPage + 1)}
        >
          Próxima
        </button>
      </Pagination>
    </Container>
  );
};

export default MyResources;
